import { defineStore } from "pinia";
import api from "@/api/api";

export const useSidebarDataStore = defineStore("sidebar-data", {
  state: () => ({
    activePluginIdsByShop: {},
    subscriptionByWorkspace: {},
    aiTokensByWorkspace: {},
    _inFlight: {},
  }),

  getters: {
    pluginIdsFor: (state) => (shopUuid) =>
      state.activePluginIdsByShop[shopUuid] || [],
    subscriptionFor: (state) => (wsUuid) =>
      state.subscriptionByWorkspace[wsUuid] || null,
    aiTokensFor: (state) => (wsUuid) =>
      state.aiTokensByWorkspace[wsUuid] || null,
  },

  actions: {
    async ensureActivePlugins(shopUuid, { force = false } = {}) {
      if (!shopUuid) return;
      if (!force && this.activePluginIdsByShop[shopUuid] !== undefined) return;
      const key = `plugins:${shopUuid}`;
      if (this._inFlight[key]) return this._inFlight[key];
      this._inFlight[key] = (async () => {
        try {
          const { data } = await api.get(`/api/shops/${shopUuid}/plugins`);
          this.activePluginIdsByShop = {
            ...this.activePluginIdsByShop,
            [shopUuid]: (data?.data || [])
              .filter((p) => p.is_active && p.is_installed)
              .map((p) => p.id),
          };
        } catch (e) {
          this.activePluginIdsByShop = {
            ...this.activePluginIdsByShop,
            [shopUuid]: [],
          };
        } finally {
          delete this._inFlight[key];
        }
      })();
      return this._inFlight[key];
    },

    async ensureSubscription(wsUuid, { force = false } = {}) {
      if (!wsUuid) return;
      if (!force && this.subscriptionByWorkspace[wsUuid] !== undefined) return;
      const key = `sub:${wsUuid}`;
      if (this._inFlight[key]) return this._inFlight[key];
      this._inFlight[key] = (async () => {
        try {
          const { data } = await api.get(`/api/workspace/${wsUuid}/subscription`);
          const s = data?.data || {};
          this.subscriptionByWorkspace = {
            ...this.subscriptionByWorkspace,
            [wsUuid]: {
              status: s.status || null,
              plan_name: s.plan_name || s.plan?.name || null,
            },
          };
        } catch (e) {
          this.subscriptionByWorkspace = {
            ...this.subscriptionByWorkspace,
            [wsUuid]: { status: null, plan_name: null },
          };
        } finally {
          delete this._inFlight[key];
        }
      })();
      return this._inFlight[key];
    },

    async ensureAiTokens(wsUuid, { force = false } = {}) {
      if (!wsUuid) return;
      if (!force && this.aiTokensByWorkspace[wsUuid] !== undefined) return;
      const key = `tokens:${wsUuid}`;
      if (this._inFlight[key]) return this._inFlight[key];
      this._inFlight[key] = (async () => {
        try {
          const { data } = await api.get(`/api/workspaces/${wsUuid}/ai-tokens`);
          this.aiTokensByWorkspace = {
            ...this.aiTokensByWorkspace,
            [wsUuid]: { available: data?.available_tokens || 0 },
          };
        } catch (e) {
          this.aiTokensByWorkspace = {
            ...this.aiTokensByWorkspace,
            [wsUuid]: { available: 0 },
          };
        } finally {
          delete this._inFlight[key];
        }
      })();
      return this._inFlight[key];
    },

    invalidatePlugins(shopUuid) {
      if (!shopUuid) return;
      const next = { ...this.activePluginIdsByShop };
      delete next[shopUuid];
      this.activePluginIdsByShop = next;
    },

    invalidateSubscription(wsUuid) {
      if (!wsUuid) return;
      const next = { ...this.subscriptionByWorkspace };
      delete next[wsUuid];
      this.subscriptionByWorkspace = next;
    },

    invalidateAiTokens(wsUuid) {
      if (!wsUuid) return;
      const next = { ...this.aiTokensByWorkspace };
      delete next[wsUuid];
      this.aiTokensByWorkspace = next;
    },
  },
});
